import React, { useEffect, useId, useRef } from "react";

const WalletNoticeModal = ({ notice, onClose, onConfirm, isSubmitting = false }) => {
  const panelRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!notice?.message || typeof document === "undefined") return undefined;

    const previouslyFocusedElement = document.activeElement;
    const focusTarget = isSubmitting
      ? panelRef.current
      : panelRef.current?.querySelector(
          '[data-wallet-alert-autofocus="true"], button:not(:disabled)'
        ) || panelRef.current;
    focusTarget?.focus();

    return () => {
      if (previouslyFocusedElement?.isConnected) {
        previouslyFocusedElement.focus();
      }
    };
  }, [isSubmitting, notice?.message, notice?.title, notice?.type]);

  if (!notice?.message) return null;

  const isSuccess = notice.type === "success";
  const isConfirm = notice.type === "confirm";
  const title = notice.title || (isSuccess ? "처리 완료" : isConfirm ? "내역 삭제" : "확인 필요");

  const handleDialogKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (!isSubmitting) onClose?.();
      return;
    }

    if (event.key !== "Tab") return;

    const focusableButtons = Array.from(
      panelRef.current?.querySelectorAll("button:not(:disabled)") || []
    );
    if (!focusableButtons.length) {
      event.preventDefault();
      panelRef.current?.focus();
      return;
    }

    const firstButton = focusableButtons[0];
    const lastButton = focusableButtons[focusableButtons.length - 1];
    if (event.shiftKey && document.activeElement === firstButton) {
      event.preventDefault();
      lastButton.focus();
    } else if (!event.shiftKey && document.activeElement === lastButton) {
      event.preventDefault();
      firstButton.focus();
    }
  };

  return (
    <div
      className="wallet-alert-backdrop"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onKeyDown={handleDialogKeyDown}
    >
      <div
        ref={panelRef}
        role="document"
        tabIndex={-1}
        className={`wallet-alert-panel ${isSuccess ? "is-success" : isConfirm ? "is-confirm" : "is-error"}`}
      >
        <span className="wallet-alert-icon" aria-hidden="true">
          {isSuccess ? (
            <svg viewBox="0 0 24 24">
              <path d="M5 12.5 9.2 17 19 7" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24">
              <path d="M12 7v6" />
              <path d="M12 17h.01" />
            </svg>
          )}
        </span>
        <strong id={titleId}>{title}</strong>
        <p id={descriptionId}>{notice.message}</p>
        {isConfirm ? (
          <div className="wallet-alert-actions">
            <button
              type="button"
              className="is-cancel"
              data-wallet-alert-autofocus="true"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {notice.cancelLabel || "취소"}
            </button>
            <button type="button" className="is-confirm" onClick={onConfirm} disabled={isSubmitting}>
              {isSubmitting ? "삭제 중" : notice.confirmLabel || "삭제"}
            </button>
          </div>
        ) : (
          <button type="button" onClick={onClose}>
            확인
          </button>
        )}
      </div>
    </div>
  );
};

export default WalletNoticeModal;
